import React from 'react'
import { FormCheckbox } from '@fs/zion-form'
import { useTranslation } from 'react-i18next'
import { Row, DescriptionTooltip } from '@fs/zion-ui'
import { NoticeInfo } from '@fs/zion-icon'
import { getMostRecentFieldValue, getNestedSubelementsByType, getPersonName, useSlsStateContext } from '@fs/zion-sls'
import FieldWrapper from '../uiHelpers/FieldWrapper'

/**
 * SelectPersonsPrefixedCheckbox component for displaying a checkbox for a person. Selected Person is automatically disabled.
 * This version requires a name prefix, allowing multiple checkbox components for the same person id.
 * Dangling persons have a tooltip instructing the user to find the name at the bottom, unless this is the "selectedInOtherArks" group.
 * @param {Object} person - object for the SLS person the checkbox is for
 * @param {Boolean} disabled - allows custom disabling
 * @param {String} namePrefix - the prefix used for this group in form values. Use "selectedInOtherArks" to disable the tooltip
 * @param {Function} onChange - callback function for when the checkbox is changed; use this to keep multiple checkboxes for the same person in sync
 * @returns {Element}
 */
const SelectPersonsPrefixedCheckbox = ({ person, disabled, namePrefix, onChange = () => {} }) => {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  if (!namePrefix) {
    throw new Error('SelectPersonsPrefixedCheckbox: namePrefix is required')
  }
  const noRelationship = t('select-persons-checkbox.no-relationship', 'No relationship')

  const relationshipToHead = getNestedSubelementsByType(person, 'RELATIONSHIP_TO_HEAD')?.[0]

  const personFullName = getPersonName({ person })

  let relationshipToHeadValue = getMostRecentFieldValue(relationshipToHead)
  if (!relationshipToHeadValue) {
    relationshipToHeadValue = `(${noRelationship})`
  } else {
    relationshipToHeadValue = `(${relationshipToHeadValue})`
  }

  return (
    <FieldWrapper>
      <Row>
        <FormCheckbox
          growX
          disabled={disabled}
          name={`${namePrefix}.${person.id}`}
          value={`${namePrefix}.${person.id}`}
          onChange={() => onChange(person.id, namePrefix)}
          label={`${personFullName} ${relationshipToHeadValue || ''}`}
        />
        {namePrefix !== 'selectedInOtherArks' && person.partitionKey !== zionSlsState.currentImageArk && (
          <DescriptionTooltip
            target={<NoticeInfo data-testid="select-persons-checkbox-tooltip-target" />}
            label={t('select-persons-checkbox.tooltip-label', 'Name on another image')}
            description={t('select-persons-checkbox.tooltip-desc', 'To see in context, find name at the bottom.')}
          />
        )}
      </Row>
    </FieldWrapper>
  )
}

export default SelectPersonsPrefixedCheckbox
